<template>
	<div v-if="isNotification" class="notification-wrapper">
		<NotificationHeader
			:unreadCount="unreadCount"
			@view-all="handleViewAll"
			@delete-all="
        handleAreYouShureModal(
          $t('Notification.deleteNotifications'),
          $t('Notification.areYouSureToDeleteAllNotifications'),
          null
        )
      "
			@toggle-unread="handleUnreadFilter"
		/>
		<div v-if="loading" class="page-loading">
			<Loading />
		</div>
		<div v-else>
			<SearchAndFilter
				v-model="searchItemQuery"
				v-model:modelValueSort="sortOrder"
				:showInput="true"
				:showSelect="false"
				:placeholder="$t('Apps.searchByAppName')"
				class="search-filter"
			/>
			<div
				class="notifications-list"
				:class="{ empty: filteredItems.length === 0 }"
			>
				<NotFoundContent
					v-if="filteredItems.length === 0"
					:message="$t('Custom.notFoundResult')"
				/>

				<TransitionGroup name="notification-list">
					<NotificationItem
						v-for="notification in filteredItems"
						:key="notification.id"
						:notification="notification"
						:isDelete="true"
						:isActionTags="isActionTags(notification)"
						@clickItem="clickItem(notification)"
						@deleteItem="
              handleAreYouShureModal(
                $t('Notification.deleteNotification'),
                $t('Notification.areUpuSureToDeleteNotification'),
                notification.id
              )
            "
					/>
				</TransitionGroup>
			</div>
		</div>
	</div>

	<AreYouSureModal
		v-if="showAreYouShureModal"
		:isVisible="showAreYouShureModal"
		:title="modalTitle"
		:message="modalMessage"
		:confirmButtonLabel="$t('Custom.delete')"
		:cancelButtonLabel="$t('Custom.cancel')"
		@result="handleModalResult"
	/>

	<NotificationComponent
		v-if="notification.show"
		:key="notification.key"
		:type="notification.type"
		:title="notification.title"
		:message="notification.message"
		@closed="!notification.show"
	/>

	<AppInfo
		v-if="isAppInfoVisible"
		:appData="appData"
		:appCountries="appCountries"
		:isBack="true"
		defaultTab="history"
		:historyId="historyId"
		@get-app="fetchData"
		@close="handleBack"
	/>
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import NotificationItem from "@/components/custom/items/NotificationItem.vue";
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import AppInfo from "@/components/user/viewComponent/app/AppInfo.vue";
import {
	deleteAllNotifications,
	deleteNotification,
	getNotifications,
	markNotificationAsRead,
	markReadAllNotifications,
} from "@/api/notification.api";
import { mapGetters } from "vuex";
import NotificationHeader from "./NotificationHeader.vue";
import { getApp, getAppCountries } from "@/api/app.api";
import { detectPlatform, extractStoreId } from "@/ultis/user/functions/storePlatform";

export default {
	name: "AppListComponent",
	components: {
		Loading,
		NotFoundContent,
		AreYouSureModal,
		NotificationComponent,
		NotificationItem,
		SearchAndFilter,
		AppInfo,
		NotificationHeader,
		AreYouSureModal,
	},
	props: {
		data: Object,
		required: false,
	},
	data() {
		return {
			notification: {
				show: false,
				key: 0,
				type: "",
				title: "",
				message: "",
			},
			loading: false,
			notifications: [],
			searchItemQuery: "",
			sortOrder: "asc",
			isNotification: true,
			isAppInfoVisible: false,
			appData: null,
			appCountries: null,
			historyId: null,
			notificationData: null,
			defaultTab: "history",
			showUnreadOnly: false,
			showAreYouShureModal: false,
			selectedNotificationId: null,
			itemId: null,
			modalTitle: "",
		};
	},
	async created() {
		await this.checkNotification();
		await this.fetchNotification();
	},
	computed: {
		filteredItems() {
			let items = this.notifications;
			if (this.showUnreadOnly) {
				items = items.filter((notification) => !notification.read_at);
			}
			items = items.filter((notification) =>
				notification.data.title
					.toLowerCase()
					.includes(this.searchItemQuery.toLowerCase())
			);

			return items;
		},
		unreadCount() {
			return this.notifications.filter((n) => !n.read_at).length;
		},
		...mapGetters("notification", ["notification"]),
	},
	methods: {
		async checkNotification() {
			this.notificationData = this.$store.state.notification.notification;
			if (this.notificationData) {
				if (this.notificationData.data.type == "app-update") {
					this.isNotification = false;
					this.appData = null;
					this.appCountries = null;
					await this.handleHistory(
						this.notificationData.data.addtionalData.url
					);
					await this.$store.dispatch("notification/setNotification", null);
					this.historyId = this.notificationData.data.addtionalData.historyId;
					this.isAppInfoVisible = true;
					if (!this.notificationData?.read_at) {
						await markNotificationAsRead(this.notificationData.id);
					}
				} else if (this.notificationData.data.type == "added-app-list-access") {
					this.isNotification = false;
					await this.$store.dispatch("notification/setNotification", null);
					this.$router.push({ name: "AppLists" });
				} else if (
					this.notificationData.data.type == "added-developer-list-access"
				) {
					this.isNotification = false;
					await this.$store.dispatch("notification/setNotification", null);
					this.$router.push({ name: "DeveloperLists" });
				}
			}
		},
		async fetchNotification() {
			this.loading = true;
			try {
				const response = await getNotifications();
				if (response.data.status) {
					this.notifications = response.data.data;
					this.updateUnreadCount();
				} else {
					this.showNotification(
						"error",
						this.$t("Custom.error"),
						response.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		async clickItem(notification) {
			const url = notification?.data?.addtionalData?.url;
			const platform = detectPlatform(url);
			const storeId = notification?.data?.addtionalData?.storeId;
			const appStoreId = storeId ? storeId : extractStoreId(url, platform);

			if (!notification?.read_at) {
				await markNotificationAsRead(notification.id);
			}
			const type = notification?.data?.type;
			const query = {};

			if (type === "app-update") {
				query.country = notification?.data?.addtionalData?.country?.countryShortName || "US";
				query.historyId = notification?.data?.addtionalData?.historyId;
			}

			switch (type) {
				case "app-update":
					this.$router.replace({
						name: "App",
						params: { storeId: appStoreId },
						query,
						});
					break;
				case "add-app-to-developer":
					this.$router.push({
						name: "App",
						params: { storeId: appStoreId },
							query: {
								country: query.country,
							}
					});
					break;
				case "added-developer-list-access":
					this.$router.push({ name: "DeveloperLists" });
					break;
				case "added-app-list-access":
					this.$router.push({ name: "AppLists" });
					break;
			}
		},
		async handleHistory(url) {
			if (!url) return;
			this.loading = true;
			try {
				await this.processAppLink(url);
				await this.getAppCountry();
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		async fetchData(url) {
			if (!url) return;
			this.loading = true;
			try {
				await this.processAppLink(url);
				await this.getAppCountry();
				this.isNotification = false;
				this.isAppInfoVisible = true;
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		async processAppLink(url = this.url) {
			try {
				const response = await getApp(url);
				if (response?.data?.status) {
					this.appData = response.data.data;
				} else {
					this.showNotification(
						"success",
						this.$t("Custom.success"),
						response.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
			}
		},
		async getAppCountry() {
			if (this.appCountries && this.appCountries.length > 0) {
				return;
			}
			try {
				const payload =
					this.appData.country.countryShortName === "US"
						? this.appData.app.id
						: this.appData.app.appId;

				const response = await getAppCountries(payload);

				if (response.data.status) {
					this.appCountries = response.data.data.map((item) => ({
						...item.country,
						appUrl: item.appUrl,
					}));
				} else {
					this.showNotification(
						"error",
						this.$t("Custom.error"),
						response.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			}
		},
		async handleViewAll() {
			this.loading = true;
			try {
				const response = await markReadAllNotifications();
				if (response.data.status) {
					await this.fetchNotification();
					this.showNotification(
						"success",
						this.$t("Custom.success"),
						response.data.msg
					);
				} else {
					this.showNotification(
						"error",
						this.$t("Custom.error"),
						response.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		async handleBack() {
			await this.fetchNotification();
			this.isNotification = true;
			this.isAppInfoVisible = false;
			this.historyId = null;
			this.appData = null;
			this.appCountries = null;
		},
		async handleDeleteAll() {
			this.loading = true;
			try {
				const responses = await deleteAllNotifications();
				if (responses.data.status) {
					await this.fetchNotification();
					this.showNotification(
						"success",
						this.$t("Custom.success"),
						responses.data.msg
					);
				} else {
					this.showNotification(
						"error",
						this.$t("Custom.error"),
						responses.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		async hanldeDleteNotification(notificationId) {
			this.loading = true;
			try {
				const response = await deleteNotification(notificationId);
				if (response.data.status) {
					await this.fetchNotification();
					this.showNotification(
						"success",
						this.$t("Custom.success"),
						response.data.msg
					);
				} else {
					this.showNotification(
						"error",
						this.$t("Custom.error"),
						response.data.msg
					);
				}
			} catch (error) {
				this.showNotification(
					"error",
					this.$t("Custom.error"),
					error.response.data.msg
				);
			} finally {
				this.loading = false;
			}
		},
		handleModalResult(result) {
			this.showAreYouShureModal = false;
			if (result && this.itemId) {
				this.hanldeDleteNotification(this.itemId);
			} else if (result && !this.itemId) {
				this.handleDeleteAll();
			}
		},
		updateUnreadCount() {
			this.unreadCount = this.notifications.filter((n) => !n.seen).length;
		},
		showNotification(type, title, message) {
			this.notification = {
				show: true,
				key: Date.now(),
				type,
				title,
				message,
			};
		},
		handleUnreadFilter(showUnread) {
			this.showUnreadOnly = showUnread;
		},
		handleAreYouShureModal(modalTitle, modalMessage, itemId) {
			this.modalTitle = modalTitle;
			this.itemId = itemId;
			this.modalMessage = modalMessage;
			this.showAreYouShureModal = true;
		},
		isActionTags(notification) {
			if (notification.data.type === "app-update") return true;
		},
	},
};
</script>

<style scoped>
.notification-wrapper {
	max-width: 1200px;
	margin: 0 auto;
	padding: 10px 20px;
	background: #ffffff;
	border-radius: 1rem;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.search-filter {
	margin-bottom: 1.5rem;
}

.notifications-list {
	margin-top: 10px;
}

.notifications-list.empty {
	align-items: center;
	justify-content: center;
}

.loading-state {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
}

/* Transition animations */
.notification-list-enter-active,
.notification-list-leave-active {
	transition: all 0.3s ease;
}

.notification-list-enter-from,
.notification-list-leave-to {
	opacity: 0;
	transform: translateX(-30px);
}

@media (max-width: 768px) {
	.notification-wrapper {
		padding: 1rem;
		margin: 0;
		border-radius: 0;
		box-shadow: none;
	}

	.notifications-list {
		margin-top: 12px;
	}

	input {
		font-size: 0.8rem !important;
	}
}
</style>
